import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { ApiError, parseProblemDetails } from './problem-details';
import {
  buildAuthHeaders,
  getAccessToken,
  isSubscriptionRequired,
  isTenantDeactivated,
  redirectToStoreDeactivated,
  redirectToSubscriptionBlocked,
  refreshAccessToken,
} from './auth-coordinator';
import { isTokenExpired } from './jwt';

/* ---- Axios instance ---- */

export const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

/* ---- Request interceptor: attach Bearer token ---- */

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Bearer + X-Tenant-Id vêm do coordenador, que é o mesmo que o stream da
  // Vapt IA usa — assim os dois clientes nunca divergem sobre quem está logado.
  for (const [name, value] of Object.entries(buildAuthHeaders())) {
    config.headers[name] = value;
  }

  return config;
});

/* ---- Response interceptor: ProblemDetails + refresh token ---- */

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status ?? 0;
    const data = error.response?.data;
    const originalRequest = error.config;

    // 403 with the subscription_required marker — redirect to the blocked page.
    // The API sends it as the `errorCode` extension of ProblemDetails
    // (ApiErrors.SubscriptionRequired); `type` carries the RFC URI.
    if (status === 403 && isSubscriptionRequired(data)) {
      redirectToSubscriptionBlocked();
      return Promise.reject(new ApiError(parseProblemDetails(data, status)));
    }

    // 403 da loja desativada pela Vapt Menu — tela própria, sem oferta de plano.
    if (status === 403 && isTenantDeactivated(data)) {
      redirectToStoreDeactivated();
      return Promise.reject(new ApiError(parseProblemDetails(data, status)));
    }

    // 401: attempt silent refresh (once per concurrent batch)
    if (
      status === 401 &&
      originalRequest &&
      !(originalRequest as unknown as Record<string, unknown>)._retried
    ) {
      const accessToken = getAccessToken();

      // Only attempt refresh if we had a token (i.e., not a login attempt)
      if (accessToken && isTokenExpired(accessToken, 0)) {
        (originalRequest as unknown as Record<string, unknown>)._retried = true;

        try {
          const newToken = await refreshAccessToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch {
          // Refresh failed — fall through to ApiError
        }
      }
    }

    // Convert to ApiError with ProblemDetails
    const problem = parseProblemDetails(data, status);
    return Promise.reject(new ApiError(problem));
  },
);
